import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import React, { FC, useEffect } from 'react'
import styled, { css } from 'styled-components'

import { Modal } from '@Components/Modal'
import { DialogBody, DialogWrapper } from '@Components/Modal/Dialog/styledElements'
import PersonalizationItemLayout from '@Components/Personalization/Layout/PersonalizationItemLayout'
import { DebossModal } from '@Components/Personalization/Modals/Deboss'
import { EngravingModal } from '@Components/Personalization/Modals/Engraving'
import { PersonalisationModal, personalizationTranslationKeys } from '@Components/Personalization/Modals/Shared'
import { UVPrintModal } from '@Components/Personalization/Modals/UVPrint'
import { PersonalizationInfoList, PersonalizationSubTitle, PersonalizationTitle } from '@Components/Personalization/Styles/Personalization.styled'
import { getPersonalizations, getPersonalizationsFromProduct } from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'

const InfoList = styled(PersonalizationInfoList)`
  margin-top: 8px;
`

const SelectorRootModal = styled(Modal)<{ selectedModal: string | null }>(({
  selectedModal,
  theme,
}) => css`
  ${DialogWrapper} {
    ${selectedModal === 'uv-print' && css`
      padding: 0;
      background: ${theme.colors.greyLightest};
    `};
  }

  ${DialogBody} {
    max-width: 440px;
    ${selectedModal === 'uv-print' && css`
      max-width: 760px;
      padding: 0;
    `};
  }
`)

export const SelectorModal: FC<Omit<Required<PersonalisationModal>, 'isChildOfModal' | 'ID' | 'updateProduct'>> = ({
  additionalIDs = undefined,
  currentModal,
  isOpen,
  product,
  setState,
}) => {
  const productPersonalisations = getPersonalizationsFromProduct(product)
  const personalisations = getPersonalizations(productPersonalisations)
  const { t } = useTranslation('products')

  useEffect(() => {
    const dialogBody = document.querySelector('#dialog-body-personalization')
    dialogBody?.scrollTo(0, 0)
  }, [currentModal])

  if (!personalisations || !isOpen) {
    return null
  }

  const closeModalByType = () => {
    setState(prevState => ({
      ...prevState,
      currentModal: null,
      isOpen: false,
    }))
  }

  const childModalOptions = { additionalIDs, isOpen, onHide: closeModalByType, product, isChildOfModal: true }

  return (
    <SelectorRootModal
      ID="personalization"
      easyClose={false}
      isOpen={isOpen}
      maxWidth="760px"
      onHide={closeModalByType}
      selectedModal={currentModal}
      variant="default"
    >
      {!currentModal && isOpen && (
        <>
          <PersonalizationTitle noSubtitle>{t('PERSONALISATION_TITLE')}</PersonalizationTitle>
          {orderBy(uniqBy(personalisations, (personalisation => personalisation.type)), ['type'], 'desc').map((personalization) => {
            const { price, type } = personalization
            const image = type === 'uv-print'
              ? 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt92740812a0dfbdf2/uv-image-child-in-watch.png'
              : personalization.image
            return (
              <PersonalizationItemLayout
                key={type}
                buttonText={`${t(personalizationTranslationKeys[`${type}-button`])} - ${price}`}
                caption={t(personalizationTranslationKeys[type])}
                image={image}
                onClick={() => {
                  setState(prevState => ({ ...prevState, currentModal: type }))
                }}
              />

            )
          })}
          <PersonalizationSubTitle marginless>{t('ABOUT_PERSONALISATION')}</PersonalizationSubTitle>
          <InfoList>
            <li>{t('PERSONALISATION_OPTION_1')}</li>
            <li>{t('PERSONALISATION_OPTION_2')}</li>
            <li>{t('PERSONALISATION_OPTION_3')}</li>
          </InfoList>
        </>
      )}
      {currentModal === 'engrave' && <EngravingModal {...childModalOptions} />}
      {currentModal === 'deboss' && <DebossModal {...childModalOptions} />}
      {currentModal === 'uv-print' && <UVPrintModal {...childModalOptions} />}
    </SelectorRootModal>
  )
}
